import { createFileRoute, redirect } from '@tanstack/react-router';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  BookOpen,
  Plus,
  Search,
  Upload,
  Edit,
  Trash2,
  Loader2,
  AlertCircle,
  RefreshCw,
  X,
  ChevronRight,
  ChevronDown,
} from 'lucide-react';
import { useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { 
  useChartOfAccounts, 
  useAccountsHierarchy,
  useDeleteAccount,
  useSeedPCN 
} from '@/hooks/queries/useAccounting';
import { toast } from '@/components/ui/toast/useToast';
import type { ChartOfAccount, AccountClass, AccountType, AccountHierarchy } from '@/types/accounting';
import { ACCOUNT_CLASS_LABELS } from '@/types/accounting';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { AccountForm } from '@/features/accounting/components/AccountForm';

export const Route = createFileRoute('/apps/accounting/chart/')({
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/login' });
    }
  },
  component: ChartOfAccountsPage,
});

function ChartOfAccountsPage() {
  const { company } = useAuthStore();
  const companyId = company?.id || '';
  
  const [searchQuery, setSearchQuery] = useState('');
  const [filterClass, setFilterClass] = useState<AccountClass | 'all'>('all');
  const [filterType, setFilterType] = useState<AccountType | 'all'>('all');
  const [viewMode, setViewMode] = useState<'list' | 'tree'>('tree');
  const [formOpen, setFormOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<ChartOfAccount | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  const filters = {
    accountClass: filterClass !== 'all' ? filterClass : undefined,
    accountType: filterType !== 'all' ? filterType : undefined,
    search: searchQuery || undefined,
  };
  
  const { data: accounts = [], isLoading, error, refetch } = useChartOfAccounts(companyId, filters);
  const { data: hierarchy = [], isLoading: isLoadingHierarchy } = useAccountsHierarchy(companyId);
  const deleteAccount = useDeleteAccount();
  const seedPCN = useSeedPCN();

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const getAccountTypeColor = (type: AccountType) => {
    const colors = {
      asset: 'bg-blue-100 text-blue-800',
      liability: 'bg-red-100 text-red-800',
      equity: 'bg-purple-100 text-purple-800',
      revenue: 'bg-green-100 text-green-800',
      expense: 'bg-orange-100 text-orange-800',
    };
    return colors[type];
  };

  const getAccountClassLabel = (classNum: AccountClass) => {
    return ACCOUNT_CLASS_LABELS[classNum].fr;
  };

  const toggleNode = (accountCode: string) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(accountCode)) {
        newSet.delete(accountCode);
      } else {
        newSet.add(accountCode);
      }
      return newSet;
    });
  };

  const handleEdit = (account: ChartOfAccount) => {
    setSelectedAccount(account);
    setFormOpen(true);
  };

  const handleNew = () => {
    setSelectedAccount(null);
    setFormOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (deleteConfirmId === id) {
      try {
        await deleteAccount.mutateAsync(id);
        toast.success(
          'تم الحذف بنجاح / Supprimé avec succès',
          'تم حذف الحساب / Le compte a été supprimé'
        );
        setDeleteConfirmId(null);
      } catch (error) {
        console.error('Failed to delete account:', error);
        toast.error(
          'خطأ / Erreur',
          'فشل حذف الحساب / Échec de la suppression'
        );
      }
    } else {
      setDeleteConfirmId(id);
      setTimeout(() => setDeleteConfirmId(null), 3000);
    }
  };

  const handleSeedPCN = async () => {
    try {
      await seedPCN.mutateAsync(companyId);
      toast.success(
        'نجح الاستيراد / Importation réussie',
        'تم استيراد المخطط المحاسبي الوطني / Plan Comptable National importé'
      );
    } catch (error) {
      console.error('Failed to seed PCN:', error);
      toast.error(
        'خطأ / Erreur',
        "فشل استيراد المخطط / Échec de l'importation"
      );
    }
  };

  const renderTreeNode = (node: AccountHierarchy) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes.has(node.account_code);
    const indent = node.level * 24;

    return (
      <div key={node.id}>
        <div
          className="flex items-center gap-3 py-2 px-3 hover:bg-gray-50 rounded-lg transition-colors group"
          style={{ paddingLeft: `${indent + 12}px` }}
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {hasChildren ? (
              <button
                onClick={() => toggleNode(node.account_code)}
                className="p-1 hover:bg-gray-200 rounded"
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-gray-500" />
                )}
              </button>
            ) : (
              <div className="w-6" />
            )}
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3">
                <span className="font-mono text-sm font-medium text-gray-700 min-w-[80px]">
                  {node.account_code}
                </span>
                <span className="text-sm text-gray-900 truncate">
                  {node.account_name}
                </span>
                {node.account_name_ar && (
                  <span className="text-sm text-gray-500 truncate" dir="rtl">
                    {node.account_name_ar}
                  </span>
                )}
              </div>
            </div>

            <Badge className={getAccountTypeColor(node.account_type)}>
              {node.account_type}
            </Badge>

            <div className="text-sm font-medium text-gray-900 min-w-[120px] text-right">
              {formatCurrency(node.current_balance)}
            </div>

            <div className="opacity-0 group-hover:opacity-100 flex gap-1 transition-opacity">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleEdit(node)}
              >
                <Edit className="h-4 w-4" />
              </Button>
              {!node.is_system_account && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(node.id)}
                  className={deleteConfirmId === node.id ? 'bg-red-100' : ''}
                >
                  {deleteConfirmId === node.id ? (
                    <X className="h-4 w-4 text-red-600" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>

        {hasChildren && isExpanded && (
          <div>
            {node.children.map(child => renderTreeNode(child))}
          </div>
        )}
      </div>
    );
  };

  const stats = {
    total: accounts.length,
    assets: accounts.filter(a => a.account_type === 'asset').reduce((sum, a) => sum + a.current_balance, 0),
    liabilities: accounts.filter(a => a.account_type === 'liability').reduce((sum, a) => sum + a.current_balance, 0),
    equity: accounts.filter(a => a.account_type === 'equity').reduce((sum, a) => sum + a.current_balance, 0),
  };

  if (isLoading || isLoadingHierarchy) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <AlertCircle className="h-12 w-12 text-red-600" />
          <p className="text-red-600">خطأ في تحميل البيانات / Erreur de chargement</p>
          <Button onClick={() => refetch()} variant="outline">
            <RefreshCw className="mr-2 h-4 w-4" />
            إعادة المحاولة / Réessayer
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <BookOpen className="h-8 w-8 text-blue-600" />
              <span>المخطط المحاسبي / Plan Comptable</span>
            </h1>
            <p className="text-gray-600 mt-1">
              إدارة الحسابات المحاسبية / Gestion du plan comptable
            </p>
          </div>
          <div className="flex gap-2">
            {accounts.length === 0 && (
              <Button onClick={handleSeedPCN} disabled={seedPCN.isPending} variant="outline">
                {seedPCN.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="mr-2 h-4 w-4" />
                )}
                استيراد PCN / Importer PCN
              </Button>
            )}
            <Button onClick={handleNew}>
              <Plus className="mr-2 h-4 w-4" />
              حساب جديد / Nouveau Compte
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">
                إجمالي الحسابات / Total Comptes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">
                الأصول / Actifs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-blue-600">
                {formatCurrency(stats.assets)}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">
                الخصوم / Passifs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-red-600">
                {formatCurrency(stats.liabilities)}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">
                رأس المال / Capitaux Propres
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-purple-600">
                {formatCurrency(stats.equity)}
              </p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="بحث... / Rechercher..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              <Select
                value={filterClass.toString()}
                onValueChange={(value) => setFilterClass(value === 'all' ? 'all' : parseInt(value) as AccountClass)}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="الفئة / Classe" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">الكل / Toutes</SelectItem>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
                    <SelectItem key={num} value={num.toString()}>
                      {num} - {getAccountClassLabel(num as AccountClass)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={filterType}
                onValueChange={(value) => setFilterType(value as AccountType | 'all')}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="النوع / Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">الكل / Tous</SelectItem>
                  <SelectItem value="asset">أصل / Actif</SelectItem>
                  <SelectItem value="liability">خصم / Passif</SelectItem>
                  <SelectItem value="equity">رأس المال / Capitaux</SelectItem>
                  <SelectItem value="revenue">إيراد / Produit</SelectItem>
                  <SelectItem value="expense">مصروف / Charge</SelectItem>
                </SelectContent>
              </Select>

              <div className="flex gap-2">
                <Button
                  variant={viewMode === 'tree' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('tree')}
                >
                  شجرة / Arbre
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                >
                  قائمة / Liste
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>الحسابات / Comptes</span>
              <span className="text-sm font-normal text-gray-500">
                {accounts.length} حساب / comptes
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {accounts.length === 0 ? (
              <div className="text-center py-12">
                <BookOpen className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  لا توجد حسابات / Aucun compte
                </h3>
                <p className="text-gray-600 mb-6">
                  ابدأ بإنشاء حساب جديد أو استيراد المخطط المحاسبي الوطني
                </p>
                <div className="flex gap-3 justify-center">
                  <Button onClick={handleSeedPCN} disabled={seedPCN.isPending}>
                    {seedPCN.isPending ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="mr-2 h-4 w-4" />
                    )}
                    استيراد PCN / Importer PCN
                  </Button>
                  <Button onClick={handleNew} variant="outline">
                    <Plus className="mr-2 h-4 w-4" />
                    حساب جديد / Nouveau Compte
                  </Button>
                </div>
              </div>
            ) : viewMode === 'tree' ? (
              <div className="space-y-1">
                {hierarchy.map(node => renderTreeNode(node))}
              </div>
            ) : (
              <div className="space-y-2">
                {accounts.map(account => (
                  <div
                    key={account.id}
                    className="flex items-center gap-3 py-3 px-3 hover:bg-gray-50 rounded-lg transition-colors group"
                  >
                    <span className="font-mono text-sm font-medium text-gray-700 min-w-[80px]">
                      {account.account_code}
                    </span>
                    <span className="text-sm text-gray-900 flex-1 truncate">
                      {account.account_name}
                    </span>
                    {account.account_name_ar && (
                      <span className="text-sm text-gray-500 truncate" dir="rtl">
                        {account.account_name_ar}
                      </span>
                    )}
                    <Badge className={getAccountTypeColor(account.account_type)}>
                      {account.account_type}
                    </Badge>
                    <div className="text-sm font-medium text-gray-900 min-w-[120px] text-right">
                      {formatCurrency(account.current_balance)}
                    </div>
                    <div className="opacity-0 group-hover:opacity-100 flex gap-1 transition-opacity">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(account)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      {!account.is_system_account && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(account.id)}
                          className={deleteConfirmId === account.id ? 'bg-red-100' : ''}
                        >
                          {deleteConfirmId === account.id ? (
                            <X className="h-4 w-4 text-red-600" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <AccountForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setSelectedAccount(null);
        }}
        account={selectedAccount}
      />
    </DashboardLayout>
  );
}